import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  UserCheck, 
  UserX,
  Mail,
  Phone,
  Building,
  Calendar,
  MoreVertical,
  CheckSquare,
  Square,
  Zap,
  Loader2,
  X,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { useApiCall, useFormSubmit } from '../../hooks/useApi';
import { userAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import { useAuth } from '../../contexts/AuthContext';

const UsersManagement = () => {
  const { notify } = useNotificationHook();
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Bulk actions state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resetPwdLoading, setResetPwdLoading] = useState(false);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, filterRole, filterStatus]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: usersPerPage,
        search: searchTerm,
        role: filterRole !== 'all' ? filterRole : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined
      };

      const response = await userAPI.getAll(params);
      
      if (response.data.success) {
        console.log('Fetched users data:', response.data.data.users);
        setUsers(response.data.data.users);
        setTotalUsers(response.data.data.pagination.total);
        setTotalPages(response.data.data.pagination.pages);
      } else {
        console.error('Failed to fetch users:', response.data.message);
        notify.error('Failed to fetch users: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      notify.error('Error fetching users: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleAddUser = async (formData) => {
    try {
      setLoading(true);
      const response = await userAPI.create(formData);
      
      if (response.data.success) {
        // Show success message
        notify.success('User created successfully!');
        // Refresh the users list
        await fetchUsers();
        setShowAddModal(false);
      } else {
        notify.error('Failed to create user: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating user:', error);
      notify.error('Error creating user: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (formData) => {
    try {
      setLoading(true);
      console.log('Updating user with data:', formData);
      const response = await userAPI.update(selectedUser.id, formData);
      
      if (response.data.success) {
        // Show success message
        notify.success('User updated successfully!');
        console.log('User update response:', response.data);
        console.log('Updated user data from response:', response.data.data?.user);
        // Refresh the users list
        await fetchUsers();
        setShowEditModal(false);
        setSelectedUser(null);
      } else {
        notify.error('Failed to update user: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating user:', error);
      notify.error('Error updating user: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    // Prevent deleting admin users
    if (user.role === 'admin') {
      notify.error('Cannot delete admin users');
      return;
    }
    
    // Prevent self-deletion
    if (user.id === currentUser?.id) {
      notify.error('Cannot delete your own account');
      return;
    }
    
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeleteLoading(true);
      const response = await userAPI.delete(userToDelete.id);
      
      if (response.data.success) {
        // Show success message
        notify.success('User deleted successfully!');
        // Refresh the users list
        await fetchUsers();
        // Close modal
        setShowDeleteModal(false);
        setUserToDelete(null);
      } else {
        notify.error('Failed to delete user: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      
      // Handle specific backend error messages
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Cannot delete this user. They may be an admin or you cannot delete your own account.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete users.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      notify.error('Error deleting user: ' + errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const response = await userAPI.update(userId, { status: newStatus });
      
      if (response.data.success) {
        // Show success message
        notify.success(`User status updated to ${newStatus}!`);
        // Refresh the users list
        await fetchUsers();
      } else {
        notify.error('Failed to update user status: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      notify.error('Error updating user status: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'active':
        return `${baseClasses} text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20`;
      case 'inactive':
        return `${baseClasses} text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20`;
      default:
        return `${baseClasses} text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20`;
    }
  };

  const getRoleBadge = (role) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (role) {
      case 'admin':
        return `${baseClasses} text-purple-700 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/20`;
      case 'client':
        return `${baseClasses} text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20`;
      default:
        return `${baseClasses} text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20`;
    }
  };

  // Bulk action functions
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
      setSelectAll(false);
    } else {
      setSelectedUsers(users.map(user => user.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedUsers.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await userAPI.bulkUpdateStatus(selectedUsers, status);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedUsers.length} users to ${status} status`);
        setSelectedUsers([]);
        setSelectAll(false);
        await fetchUsers();
      } else {
        notify.error('Failed to update users: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating users:', error);
      notify.error('Error updating users: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkRoleUpdate = async (role) => {
    if (selectedUsers.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await userAPI.bulkUpdateRole(selectedUsers, role);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedUsers.length} users to ${role} role`);
        setSelectedUsers([]);
        setSelectAll(false);
        await fetchUsers();
      } else {
        notify.error('Failed to update users: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating users:', error);
      notify.error('Error updating users: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} users? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await userAPI.bulkDelete(selectedUsers);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedUsers.length} users`);
        setSelectedUsers([]);
        setSelectAll(false);
        await fetchUsers();
      } else {
        notify.error('Failed to delete users: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting users:', error);
      notify.error('Error deleting users: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedUsers.length > 0);
  }, [selectedUsers]);

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Users Management
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              Manage user accounts, roles, and permissions
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center touch-manipulation"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Add User</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4 sm:p-6 mb-4 sm:mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Users className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{totalUsers}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <UserCheck className="h-4 w-4 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter(u => u.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <UserCheck className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Admins</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter(u => u.role === 'admin').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-1.5 sm:p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
              <Users className="h-4 w-4 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-2 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Clients</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {users.filter(u => u.role === 'client').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm mb-4 sm:mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="client">Client</option>
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <span className="text-xs sm:text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleBulkStatusUpdate('active')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50 touch-manipulation"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <UserCheck className="h-3 w-3 mr-1" />}
                  <span className="hidden sm:inline">Set Active</span>
                  <span className="sm:hidden">Active</span>
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('inactive')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50 touch-manipulation"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <UserX className="h-3 w-3 mr-1" />}
                  <span className="hidden sm:inline">Set Inactive</span>
                  <span className="sm:hidden">Inactive</span>
                </button>
                
                <button
                  onClick={() => handleBulkRoleUpdate('admin')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-md flex items-center disabled:opacity-50 touch-manipulation"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  <span className="hidden sm:inline">Make Admin</span>
                  <span className="sm:hidden">Admin</span>
                </button>
                
                <button
                  onClick={() => handleBulkRoleUpdate('client')}
                  disabled={bulkActionLoading}
                  className="px-2 sm:px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center disabled:opacity-50 touch-manipulation"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  <span className="hidden sm:inline">Make Client</span>
                  <span className="sm:hidden">Client</span>
                </button>
              </div>
            </div>
            
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="px-2 sm:px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50 touch-manipulation"
            >
              {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
              <span className="hidden sm:inline">Delete Selected</span>
              <span className="sm:hidden">Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center hover:text-blue-600 dark:hover:text-blue-400 touch-manipulation"
                  >
                    {selectAll ? <CheckSquare className="h-4 w-4 mr-1 sm:mr-2" /> : <Square className="h-4 w-4 mr-1 sm:mr-2" />}
                    <span className="hidden sm:inline">Select All</span>
                    <span className="sm:hidden">All</span>
                  </button>
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  CRM
                </th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-3 sm:px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 sm:px-6 py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleSelectUser(user.id)}
                        className="flex items-center hover:text-blue-600 dark:hover:text-blue-400 touch-manipulation"
                      >
                        {selectedUsers.includes(user.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                          <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                            {user.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-2 sm:ml-4">
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate max-w-24 sm:max-w-none">
                            {user.name}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                            ID: {user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white">
                        <div className="flex items-center">
                          <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          <span className="truncate max-w-32 sm:max-w-none">{user.email}</span>
                        </div>
                        {user.phone && (
                          <div className="flex items-center mt-1">
                            <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="truncate max-w-24 sm:max-w-none">{user.phone}</span>
                          </div>
                        )}
                        {user.company && (
                          <div className="flex items-center mt-1">
                            <Building className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="truncate max-w-24 sm:max-w-none">{user.company}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.crm_enabled 
                          ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20'
                          : 'text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20'
                      }`}>
                        {user.crm_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleStatusToggle(user.id, user.status)}
                          className={`p-1 rounded touch-manipulation ${
                            user.status === 'active' 
                              ? 'text-red-600 hover:text-red-900' 
                              : 'text-green-600 hover:text-green-900'
                          }`}
                          title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {user.status === 'active' ? <UserX className="h-3 w-3 sm:h-4 sm:w-4" /> : <UserCheck className="h-3 w-3 sm:h-4 sm:w-4" />}
                        </button>
                        <button
                          onClick={() => {
                            console.log('Edit button clicked for user:', user);
                            setSelectedUser(user);
                            setShowEditModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded touch-manipulation"
                          title="Edit"
                        >
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      <button
                        onClick={() => {
                          setResetPwdUser(user);
                          setResetPwdValue('');
                          setShowResetPwd(true);
                        }}
                        className="text-orange-600 hover:text-orange-900 p-1 rounded touch-manipulation"
                        title="Reset Password"
                      >
                        <Lock className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={user.role === 'admin' || user.id === currentUser?.id}
                          className={`p-1 rounded touch-manipulation ${
                            user.role === 'admin' || user.id === currentUser?.id
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-900'
                          }`}
                          title={
                            user.role === 'admin' 
                              ? 'Cannot delete admin users' 
                              : user.id === currentUser?.id 
                                ? 'Cannot delete your own account'
                                : 'Delete'
                          }
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <UserModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddUser}
          title="Add New User"
          loading={loading}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <UserModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handleEditUser}
          title="Edit User"
          user={selectedUser}
          loading={loading}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDeleteUser}
        user={userToDelete}
        loading={deleteLoading}
      />

      {/* Reset Password Modal */}
      {showResetPwd && resetPwdUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reset Password</h3>
              <button onClick={() => setShowResetPwd(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">User: {resetPwdUser.name} ({resetPwdUser.email})</p>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
            <input
              type="password"
              value={resetPwdValue}
              onChange={(e) => setResetPwdValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Enter new password"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowResetPwd(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md">Cancel</button>
              <button
                onClick={async () => {
                  if (!resetPwdValue || resetPwdValue.length < 6) {
                    notify.error('Password must be at least 6 characters long');
                    return;
                  }
                  try {
                    setResetPwdLoading(true);
                    const resp = await userAPI.resetPassword(resetPwdUser.id, resetPwdValue);
                    if (resp.data.success) {
                      notify.success('Password reset successfully!');
                      setShowResetPwd(false);
                    } else {
                      notify.error(resp.data.message || 'Failed to reset password');
                    }
                  } catch (err) {
                    notify.error(err.response?.data?.message || err.message || 'Failed to reset password');
                  } finally {
                    setResetPwdLoading(false);
                  }
                }}
                disabled={resetPwdLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
              >
                {resetPwdLoading ? 'Saving...' : 'Save Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// User Modal Component
const UserModal = ({ isOpen, onClose, onSubmit, title, user = null, loading = false }) => {
  const { notify } = useNotificationHook();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    address: user?.address || '',
    whatsapp: user?.whatsapp || '',
    role: user?.role || 'client',
    status: user?.status || 'active',
    password: '',
    crm_email: user?.crm_email || '',
    crm_username: user?.crm_username || '',
    crm_password: user?.crm_password || '',
    crm_enabled: user?.crm_enabled || false
  });

  // Update form data when user prop changes
  useEffect(() => {
    console.log('UserModal: user prop changed:', user);
    if (user) {
      const newFormData = {
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        address: user.address || '',
        whatsapp: user.whatsapp || '',
        role: user.role || 'client',
        status: user.status || 'active',
        password: '',
        crm_email: user.crm_email || '',
        crm_username: user.crm_username || '',
        crm_password: user.crm_password || '',
        crm_enabled: user.crm_enabled || false
      };
      console.log('UserModal: setting form data:', newFormData);
      setFormData(newFormData);
    } else {
      const newFormData = {
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        whatsapp: '',
        role: 'client',
        status: 'active',
        password: '',
        crm_email: '',
        crm_username: '',
        crm_password: '',
        crm_enabled: false
      };
      console.log('UserModal: resetting form data:', newFormData);
      setFormData(newFormData);
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (!submitData.password) {
      delete submitData.password; // Don't update password if empty
    }
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    console.log('UserModal: handleChange:', { name, value: newValue, type });
    const updatedFormData = {
      ...formData,
      [name]: newValue
    };
    console.log('UserModal: updated form data:', updatedFormData);
    setFormData(updatedFormData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{title}</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Company
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Enter complete address"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              WhatsApp Number
            </label>
            <input
              type="tel"
              name="whatsapp"
              value={formData.whatsapp}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="+91-9876543210"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="client">Client</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password {user && '(leave empty to keep current)'}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required={!user}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* CRM Section */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-6">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">CRM Access</h4>
            
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                name="crm_enabled"
                checked={formData.crm_enabled}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable CRM Access
              </label>
            </div>

            {formData.crm_enabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CRM Email
                  </label>
                  <input
                    type="email"
                    name="crm_email"
                    value={formData.crm_email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="crm@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CRM Username
                  </label>
                  <input
                    type="text"
                    name="crm_username"
                    value={formData.crm_username}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="username"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CRM Password
                  </label>
                  <input
                    type="text"
                    name="crm_password"
                    value={formData.crm_password}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="password"
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : user ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, user, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
              <Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete User
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-full">
                <UserX className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Are you sure you want to delete this user?
                </p>
                {user && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Name:</span> {user.name}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Email:</span> {user.email}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Role:</span> {user.role}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                  Warning
                </p>
                <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                  <li>• All user data will be permanently deleted</li>
                  <li>• Associated projects and orders will be affected</li>
                  <li>• This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 sm:space-x-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                <span>Delete User</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;
